import classNames from 'classnames';

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { formatDaysPlural } from '../../../utils';
import { Select } from './select';

/** Один приём: час/значение времени и количество таблеток за приём. */
export type TimeWithCount = {
  timeId: number;
  value: string;
  count: number;
  /** Справочник «до/после еды» — уходит в receipt_items при создании заказа. */
  eatingId?: number;
};

export type IntakeMethod = 'before' | 'after' | 'with';

export const TIMES_PER_INTERVAL_OPTIONS = [
  { label: '1 раз', value: 1 },
  { label: '2 раза', value: 2 },
  { label: '3 раза', value: 3 },
  { label: '4 раза', value: 4 },
  { label: '5 раз', value: 5 },
];

const getIntervalDayLabel = (d: number) => {
  if (d === 1) return '1 день';
  if (d <= 4) return `${d} дня`;
  return `${d} дней`;
};

/**
 * Интервалы, которые понимает справочник частоты на бэкенде: «Раз в два дня»,
 * «Раз в три дня» и неделя. Остальные значения отправить нечем.
 */
export const INTERVAL_DAYS_OPTIONS = [2, 3, 7].map((d) => ({
  label: getIntervalDayLabel(d),
  value: d,
}));

/** Часть суток по времени (08:00 → Утром, 14:00 → Днём и т.д.) */
export function getPartOfDay(timeValue: string): string {
  const match = String(timeValue).match(/^(\d{1,2})/);
  const hour = match ? parseInt(match[1], 10) : 12;
  if (hour >= 0 && hour < 5) return 'Ночью';
  if (hour >= 5 && hour < 12) return 'Утром';
  if (hour >= 12 && hour < 18) return 'Днём';
  return 'Вечером';
}

export function hourFromTimeValue(timeValue: string): number {
  const match = String(timeValue).match(/^(\d{1,2})/);
  const h = match ? parseInt(match[1], 10) : 8;
  return Math.max(0, Math.min(23, h));
}

export const PencilIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.24219 16.8751H3.75C3.58424 16.8751 3.42527 16.8092 3.30806 16.692C3.19085 16.5748 3.125 16.4158 3.125 16.2501V12.7579C3.12472 12.6767 3.14044 12.5963 3.17128 12.5212C3.20211 12.4461 3.24745 12.3779 3.30469 12.3204L12.6797 2.94537C12.7378 2.88631 12.8072 2.83942 12.8836 2.80741C12.9601 2.7754 13.0421 2.75891 13.125 2.75891C13.2079 2.75891 13.2899 2.7754 13.3664 2.80741C13.4428 2.83942 13.5122 2.88631 13.5703 2.94537L17.0547 6.42974C17.1137 6.4879 17.1606 6.55722 17.1926 6.63367C17.2247 6.71012 17.2411 6.79217 17.2411 6.87505C17.2411 6.95793 17.2247 7.03999 17.1926 7.11644C17.1606 7.19289 17.1137 7.26221 17.0547 7.32037L7.67969 16.6954C7.62216 16.7526 7.55391 16.7979 7.47884 16.8288C7.40376 16.8596 7.32335 16.8753 7.24219 16.8751V16.8751Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.625 5L15 9.375"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const TrashIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.875 4.375H3.125"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.125 8.125V13.125"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.875 8.125V13.125"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.625 4.375V16.25C15.625 16.4158 15.5592 16.5747 15.4419 16.6919C15.3247 16.8092 15.1658 16.875 15 16.875H5C4.83424 16.875 4.67527 16.8092 4.55806 16.6919C4.44085 16.5747 4.375 16.4158 4.375 16.25V4.375"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.125 4.375V3.125C13.125 2.79348 12.9933 2.47554 12.7589 2.24112C12.5245 2.0067 12.2065 1.875 11.875 1.875H8.125C7.79348 1.875 7.47554 2.0067 7.24112 2.24112C7.0067 2.47554 6.875 2.79348 6.875 3.125V4.375"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Шеврон «вниз» — в макете все раскрывашки смотрят вниз. */
export const ChevronDownIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PlusIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3v10M3 8h10" />
  </svg>
);

export const WarnIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
      stroke="#E06AD4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Розовый блок с причиной, по которой шаг или форма не пропускают дальше. */
export const ErrorBlock = ({
  message,
  className = '',
}: {
  message: string;
  className?: string;
}) => (
  <div
    className={`flex w-full items-start gap-[12px] rounded-[12px] p-[16px] ${className}`}
    style={{ background: '#e06ad414' }}
  >
    <WarnIcon className="shrink-0" />
    <div className="flex flex-1 flex-col gap-[8px]">
      <p className="text-sm leading-[1.125rem] text-[#151515]/50">{message}</p>
    </div>
  </div>
);

/** Подпись поля: 14/16 на мобилке, 16/20 на десктопе (по макету). */
export const FieldLabel = ({
  children,
  size = 'responsive',
}: {
  children: ReactNode;
  size?: 'responsive' | 'base';
}) => (
  <span
    className={classNames(
      'self-stretch font-normal text-[#151515] opacity-50',
      size === 'base'
        ? 'text-base leading-5'
        : 'text-sm leading-4 md:text-base md:leading-5',
    )}
  >
    {children}
  </span>
);

/** Радио-метка 20×20: рамка #2c9aee, выбранное — залито с белой точкой. */
const RadioMark = ({ checked }: { checked: boolean }) => (
  <span className="relative block size-5 shrink-0">
    <span
      className={classNames(
        'absolute inset-0 rounded-full',
        checked ? 'bg-[#2c9aee]' : 'border border-[#2c9aee]',
      )}
    />
    {checked && (
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="size-1.5 rounded-full bg-white" />
      </span>
    )}
  </span>
);

export const RadioOption = ({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
}) => (
  <label className="flex cursor-pointer items-center gap-2">
    <RadioMark checked={checked} />
    <span className="text-base font-normal leading-6 text-[#151515]">
      {label}
    </span>
    <input
      type="radio"
      className="sr-only"
      checked={checked}
      onChange={onChange}
    />
  </label>
);

/** «1 раз каждые 3 дня» — показывается для частоты «раз в несколько дней». */
export const IntervalField = ({
  timesPerInterval,
  intervalDays,
  onTimesChange,
  onDaysChange,
}: {
  timesPerInterval: number;
  intervalDays: number;
  onTimesChange: (v: number) => void;
  onDaysChange: (v: number) => void;
}) => (
  // min-w-0 обязателен: без него flex-элементы не сжимаются меньше своего
  // min-content, и на мобилке правый селект вылезал за экран, растягивая
  // страницу по горизонтали. Внутри селектов на мобилке ужимаем отступы.
  <div className="flex w-full items-center gap-2 self-stretch rounded-[20px] bg-[#f1f7fa] p-2 md:gap-4 [&_button:has(>span)]:gap-1 [&_button:has(>span)]:px-2 md:[&_button:has(>span)]:gap-3 md:[&_button:has(>span)]:px-5 [&_button>span]:text-sm md:[&_button>span]:text-base [&_button>svg]:size-4 md:[&_button>svg]:size-6">
    <div className="min-w-0 flex-1">
      <Select
        value={timesPerInterval}
        onChange={onTimesChange}
        options={TIMES_PER_INTERVAL_OPTIONS}
        placeholder="1 раз"
      />
    </div>
    <span className="shrink-0 text-sm font-normal leading-5 text-[#151515] md:text-base">
      каждые
    </span>
    <div className="min-w-0 flex-1">
      <Select
        value={intervalDays}
        onChange={onDaysChange}
        options={INTERVAL_DAYS_OPTIONS}
        placeholder="3 дня"
      />
    </div>
  </div>
);

/** Счётчик дней курса: число можно вписать вручную,
 * справа необязательная корзина препарата. */
export const DurationField = ({
  value,
  onChange,
  onDelete,
  label = 'Как долго принимать',
}: {
  value: number;
  onChange: (v: number) => void;
  onDelete?: () => void;
  label?: string;
}) => {
  const current = value || 1;
  // Черновик ручного ввода: null — не редактируем, показываем value.
  // Валидные значения (≥ 1) уходят наружу сразу при вводе; пустая/невалидная
  // строка живёт только локально и на blur возвращается к текущему значению.
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft ?? String(current);
  const wordValue = draft ? parseInt(draft, 10) : current;

  // Внешнее изменение value (кнопки ±, reset формы после добавления
  // препарата) гасит черновик: в Safari/iOS клик по кнопке не блюрит инпут,
  // и без сброса поле продолжало бы показывать устаревший ввод.
  useEffect(() => {
    setDraft(null);
  }, [value]);

  return (
    <div className="flex w-full flex-col gap-3">
      <FieldLabel size="base">{label}</FieldLabel>
      <div className="flex w-full items-end gap-3">
        <div className="inline-flex w-full items-center justify-center gap-3 md:w-[312px]">
          <button
            type="button"
            className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#f7f8f9]"
            onClick={() => onChange(Math.max(1, current - 1))}
            aria-label="Уменьшить"
          >
            <svg
              className="size-5"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#343330"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M3.125 10h13.75" />
            </svg>
          </button>
          <label className="flex flex-1 cursor-text items-center justify-start gap-3 rounded-xl border border-[#afc4d1] p-[18px] transition focus-within:border-[#0788f1]">
            {/* w-0 гасит интринсик-ширину инпута (size=20 ≈ 170px), иначе
                в тесной строке с корзиной ряд выталкивает кнопки за экран;
                min-w-[3ch] не даёт flex-shrink схлопнуть цифры в 0px */}
            <input
              type="text"
              inputMode="numeric"
              value={displayValue}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                setDraft(digits);
                const parsed = parseInt(digits, 10);
                if (!Number.isNaN(parsed) && parsed >= 1) onChange(parsed);
              }}
              onBlur={() => setDraft(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="w-0 min-w-[3ch] flex-1 bg-transparent text-base font-normal leading-5 text-[#151515] opacity-50 outline-none"
              aria-label={label}
            />
            <span className="text-base font-normal leading-5 text-[#151515] opacity-50">
              {formatDaysPlural(wordValue)}
            </span>
          </label>
          <button
            type="button"
            className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#f7f8f9]"
            onClick={() => onChange(current + 1)}
            aria-label="Увеличить"
          >
            <svg
              className="size-5"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#343330"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 3.125v13.75M3.125 10h13.75" />
            </svg>
          </button>
        </div>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto flex size-14 shrink-0 items-center justify-center rounded-full bg-[#f7f8f9] text-[#151515]"
            aria-label="Удалить препарат"
          >
            <TrashIcon className="size-5" />
          </button>
        )}
      </div>
    </div>
  );
};
